import { useState } from "react";
import { Check, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const shortDate = (at) => {
  if (!at) return "";
  const [y, m, d] = at.slice(0, 10).split("-");
  return `${Number(m)}.${Number(d)}.${y.slice(2)}`;
};

/** Shared, stamped, append-only — one tight line each, click to read the whole thing. */
export default function NoteLog({ notes, onAdd, testId }) {
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await onAdd(text.trim());
      setText("");
      setAdding(false);
    } finally { setBusy(false); }
  };

  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Notes To Self
        </p>
        <button onClick={() => setAdding((v) => !v)} data-testid="ops-note-add"
                title="Add a note"
                className="rounded-md border border-slate-200 px-1.5 hover:border-slate-400">
          {adding ? <X className="h-3.5 w-3.5 text-slate-500" />
            : <Plus className="h-3.5 w-3.5 text-slate-500" />}
        </button>
      </div>
      {adding && (
        <div className="mt-2 space-y-2">
          <Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)}
                    className="text-sm" data-testid="ops-note-input" />
          <div className="flex justify-end">
            <Button size="sm" onClick={submit} disabled={busy || !text.trim()}
                    data-testid="ops-note-save">
              <Check className="mr-1.5 h-3.5 w-3.5" /> Add note
            </Button>
          </div>
        </div>
      )}
      <ul className="mt-1 divide-y divide-slate-100">
        {notes.length === 0 && !adding && (
          <li className="py-1 text-xs text-slate-400">No notes yet</li>
        )}
        {notes.map((n) => {
          const expanded = open === n.id;
          return (
            <li key={n.id} data-testid={`ops-note-${n.id}`}>
              <button onClick={() => setOpen(expanded ? null : n.id)}
                      data-testid={`ops-note-toggle-${n.id}`}
                      className="flex w-full items-baseline gap-2 py-1 text-left">
                <span className={`min-w-0 flex-1 text-xs text-slate-700 ${
                  expanded ? "whitespace-pre-wrap" : "truncate"}`}>{n.text}</span>
                <span className="shrink-0 text-[11px] text-slate-400">{shortDate(n.at)}</span>
              </button>
              {expanded && (
                <p className="pb-1 text-[11px] text-slate-400">{n.author}</p>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
